import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';
import { 
  Image, 
  Loader2, 
  RefreshCw, 
  Search, 
  Download, 
  Copy, 
  Eye,
  Grid3X3,
  List,
  X,
  ChevronRight,
  Sparkles,
  Wand2,
  ArrowRight,
  Maximize2,
  RotateCcw,
  Palette,
  Sun,
  Eraser,
  Layers,
  Zap,
  Check,
  Send
} from 'lucide-react';

// Enhancement presets
const ENHANCEMENT_PRESETS = [
  // Basic
  { id: 'enhance', name: 'Auto Enhance', icon: Sparkles, prompt: 'Enhance this product image with better lighting, sharper details, and professional color grading. Keep the product exactly the same but make it look more premium and appealing.', color: 'indigo', category: 'basic' },
  { id: 'brighten', name: 'Brighten', icon: Sun, prompt: 'Make this image brighter and more vibrant while maintaining natural colors. Improve exposure and reduce any dark shadows.', color: 'amber', category: 'basic' },
  { id: 'color', name: 'Color Pop', icon: Palette, prompt: 'Make the colors in this image more vivid and eye-catching. Enhance saturation tastefully, add slight warmth, and make it stand out for social media ads.', color: 'purple', category: 'basic' },
  
  // Backgrounds
  { id: 'background', name: 'White BG', icon: Eraser, prompt: 'Replace the background with a clean, pure white studio background while keeping the main product/subject perfectly intact. Professional e-commerce style.', color: 'emerald', category: 'background' },
  { id: 'gradient', name: 'Gradient BG', icon: Palette, prompt: 'Replace the background with a beautiful modern gradient (soft pink to purple or blue to teal). Keep the product intact, professional look.', color: 'pink', category: 'background' },
  { id: 'nature', name: 'Nature BG', icon: Layers, prompt: 'Place this product in a beautiful natural outdoor setting - garden, forest, or beach. Make it look naturally placed and professionally shot.', color: 'green', category: 'background' },
  { id: 'urban', name: 'Urban BG', icon: Layers, prompt: 'Place this product in a modern urban setting - trendy cafe, city street, or contemporary interior. Lifestyle marketing style.', color: 'gray', category: 'background' },
  { id: 'minimal', name: 'Minimal BG', icon: Eraser, prompt: 'Create an ultra-minimalist background - single solid color (soft gray, beige, or pastel), very clean and modern. Focus entirely on the product.', color: 'slate', category: 'background' },
  
  // Scenes
  { id: 'lifestyle', name: 'Lifestyle', icon: Layers, prompt: 'Transform this product photo into a lifestyle scene. Add a beautiful, contextual background that fits the product category. Make it look like a professional marketing photo.', color: 'rose', category: 'scene' },
  { id: 'premium', name: 'Premium', icon: Zap, prompt: 'Transform this into a luxury, high-end product shot. Add subtle shadows, premium lighting effects, and a sophisticated feel. Magazine-quality result.', color: 'yellow', category: 'scene' },
  { id: 'magazine', name: 'Magazine', icon: Layers, prompt: 'Style this like a high-end magazine cover or editorial shot. Dramatic lighting, perfect composition, fashion/lifestyle magazine quality.', color: 'red', category: 'scene' },
  { id: '3dpop', name: '3D Pop', icon: Zap, prompt: 'Create a 3D pop-out effect where the product appears to jump out of the frame. Add depth, shadows, and a dynamic feel.', color: 'blue', category: 'scene' },
  
  // Styles
  { id: 'retro', name: 'Retro', icon: Palette, prompt: 'Apply a vintage retro filter - 70s/80s nostalgic look with warm tones, film grain, and faded colors. Hipster aesthetic.', color: 'orange', category: 'style' },
  { id: 'neon', name: 'Neon Glow', icon: Zap, prompt: 'Add neon glow effects and cyberpunk lighting. Vibrant neon colors (pink, blue, purple) creating a futuristic, nightclub atmosphere.', color: 'fuchsia', category: 'style' },
  { id: 'pastel', name: 'Pastel Soft', icon: Sun, prompt: 'Transform with soft pastel color palette. Dreamy, gentle tones - soft pink, baby blue, mint green. Instagram aesthetic, very gentle and appealing.', color: 'pink', category: 'style' },
  { id: 'dark', name: 'Dark Moody', icon: Layers, prompt: 'Create a dark, moody, dramatic atmosphere. Deep shadows, rich blacks, selective lighting highlighting the product. Luxury and mystery.', color: 'zinc', category: 'style' },
  
  // Seasonal
  { id: 'summer', name: 'Summer', icon: Sun, prompt: 'Add bright summer vibes - sunny lighting, beach/pool atmosphere, warm golden tones, vacation feeling. Perfect for summer campaigns.', color: 'amber', category: 'seasonal' },
  { id: 'autumn', name: 'Autumn', icon: Palette, prompt: 'Apply warm autumn colors - orange, brown, golden tones. Cozy fall atmosphere with warm lighting. Perfect for fall campaigns.', color: 'orange', category: 'seasonal' },
  { id: 'winter', name: 'Winter', icon: Sparkles, prompt: 'Create cozy winter atmosphere - soft snow, warm indoor lighting, holiday feeling. Blue-white tones with warm accent lighting.', color: 'cyan', category: 'seasonal' },
  { id: 'holiday', name: 'Holiday', icon: Sparkles, prompt: 'Add festive holiday atmosphere - subtle sparkles, warm lights, celebration feeling. Works for Christmas, New Year, or any festive occasion.', color: 'red', category: 'seasonal' },
  
  // Professional
  { id: 'shadow', name: 'Add Shadow', icon: Layers, prompt: 'Add professional product shadow - soft natural shadow beneath the product for depth. Clean e-commerce style with proper grounding.', color: 'gray', category: 'pro' },
];

export default function MetaAIEnhancerLab() {
  const { selectedBrand } = useAuth();
  
  // Connection state
  const [adAccountId, setAdAccountId] = useState(null);
  const [adAccountName, setAdAccountName] = useState('');
  const [connectionLoading, setConnectionLoading] = useState(true);
  
  // Media state
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  
  // UI state
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Enhancement state
  const [showEnhanceModal, setShowEnhanceModal] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhancedImage, setEnhancedImage] = useState(null); // { mimeType, data (base64) }
  const [enhanceError, setEnhanceError] = useState('');
  
  // Pagination
  const [imagesCursor, setImagesCursor] = useState(null);
  const [hasMoreImages, setHasMoreImages] = useState(false);

  const brandId = useMemo(() => {
    return selectedBrand?.id || localStorage.getItem('currentBrandId') || sessionStorage.getItem('currentBrandId') || '';
  }, [selectedBrand?.id]);

  // Resolve Meta Ads connection on mount
  useEffect(() => {
    if (brandId) {
      resolveConnection();
    }
  }, [brandId]);

  // Fetch media when ad account is resolved
  useEffect(() => {
    if (adAccountId) {
      fetchImages();
    }
  }, [adAccountId]);

  const resolveConnection = async () => {
    setConnectionLoading(true);
    try {
      const resp = await fetch('https://api.glowytics.com/api/auth/connections', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        }
      });
      const data = await resp.json();
      const connections = Array.isArray(data) ? data : (Array.isArray(data?.connections) ? data.connections : []);
      
      const metaAdsConn = connections.find((c) => c?.platform === 'META_ADS' && c?.status === 'ACTIVE');
      
      if (metaAdsConn) {
        const accountId = metaAdsConn.customerId;
        setAdAccountId(accountId);
        setAdAccountName(metaAdsConn.accountName || `Ad Account ${accountId}`);
      } else {
        toast.error('Meta Ads connection not found. Please connect from Settings.');
      }
    } catch (err) {
      console.error('Connection resolve error:', err);
      toast.error('Failed to get connection info');
    } finally {
      setConnectionLoading(false);
    }
  };

  const fetchImages = async (cursor = null) => {
    if (!adAccountId) return;
    setLoadingImages(true);
    
    try {
      let url = `https://api.glowytics.com/api/meta-ads/media-library/images?adAccountId=${adAccountId}`;
      if (cursor) {
        url += `&after=${cursor}`;
      }
      
      const resp = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        }
      });
      
      const data = await resp.json();
      
      if (data.success) {
        if (cursor) {
          setImages(prev => [...prev, ...data.images]);
        } else {
          setImages(data.images || []);
        }
        setImagesCursor(data.paging?.cursors?.after || null);
        setHasMoreImages(!!data.paging?.next);
      } else {
        toast.error(data.error || 'Failed to load images');
      }
    } catch (err) {
      console.error('Fetch images error:', err);
      toast.error('Error loading images');
    } finally {
      setLoadingImages(false);
    }
  };

  const handleRefresh = () => {
    setImages([]);
    setImagesCursor(null);
    fetchImages();
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Filter images by search term
  const filteredImages = images.filter(img => 
    (img.name || img.hash || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const loadMore = () => {
    if (imagesCursor) {
      fetchImages(imagesCursor);
    }
  };

  // Open enhance modal for selected image
  const openEnhanceModal = (image) => {
    setSelectedImage(image);
    setSelectedPreset(null);
    setCustomPrompt('');
    setEnhancedImage(null);
    setEnhanceError('');
    setShowEnhanceModal(true);
  };

  const closeEnhanceModal = () => {
    if (isEnhancing) return;
    setShowEnhanceModal(false);
    setSelectedImage(null);
    setSelectedPreset(null);
    setCustomPrompt('');
    setEnhancedImage(null);
    setEnhanceError('');
  };

  // Convert image URL to base64
  const urlToBase64 = async (url) => {
    try {
      const resp = await fetch(url);
      const blob = await resp.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result;
          const [prefix, base64] = dataUrl.split(',');
          const mimeType = (prefix.match(/data:([^;]+);base64/i)?.[1]) || 'image/jpeg';
          resolve({ mimeType, data: base64 });
        };
        reader.onerror = () => reject(new Error('Failed to read image'));
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      throw new Error('Failed to load image: ' + err.message);
    }
  };

  // Enhance image with Gemini
  const handleEnhance = async () => {
    if (!selectedImage || isEnhancing) return;
    
    const prompt = selectedPreset 
      ? ENHANCEMENT_PRESETS.find(p => p.id === selectedPreset)?.prompt 
      : customPrompt.trim();
    
    if (!prompt) {
      toast.error('Please select a preset or enter a custom prompt');
      return;
    }

    setIsEnhancing(true);
    setEnhanceError('');
    setEnhancedImage(null);

    try {
      // Get image as base64
      const imageUrl = selectedImage.url || selectedImage.url_128;
      if (!imageUrl) {
        throw new Error('No image URL available');
      }

      const imageData = await urlToBase64(imageUrl);
      
      // Send to Gemini
      const resp = await fetch('https://api.glowytics.com/api/ai/gemini/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
        body: JSON.stringify({
          model: 'gemini-2.5-flash-image', // Nano Banana
          temperature: 0.7,
          messages: [
            {
              role: 'user',
              content: prompt,
              images: [{ mimeType: imageData.mimeType, data: imageData.data }]
            }
          ]
        })
      });

      const txt = await resp.text();
      let json;
      try { json = JSON.parse(txt); } catch { json = { message: txt }; }
      
      if (!resp.ok) {
        const errMsg = json?.error || json?.message || resp.statusText;
        throw new Error(errMsg);
      }

      // Check if we got an image back
      if (json?.images && json.images.length > 0) {
        setEnhancedImage(json.images[0]);
        toast.success('Image enhanced successfully!');
      } else {
        // Gemini might return text instead of image
        setEnhanceError(json?.message || 'No image was generated. Try a different prompt.');
      }
    } catch (err) {
      console.error('Enhancement error:', err);
      setEnhanceError(err.message || 'Failed to enhance image');
      toast.error('Enhancement failed');
    } finally {
      setIsEnhancing(false);
    }
  };

  // Download enhanced image
  const downloadEnhanced = () => {
    if (!enhancedImage?.data) return;
    
    const link = document.createElement('a');
    link.href = `data:${enhancedImage.mimeType || 'image/png'};base64,${enhancedImage.data}`;
    link.download = `enhanced-${selectedImage?.hash || 'image'}.${enhancedImage.mimeType?.includes('jpeg') ? 'jpg' : 'png'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Image downloaded!');
  };

  // Upload enhanced image to Meta
  const [isUploadingToMeta, setIsUploadingToMeta] = useState(false);
  
  const uploadToMeta = async () => {
    if (!enhancedImage?.data || !adAccountId || isUploadingToMeta) return;
    
    setIsUploadingToMeta(true);
    try {
      // Convert base64 to blob
      const byteCharacters = atob(enhancedImage.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: enhancedImage.mimeType || 'image/png' });
      
      // Create file from blob
      const fileName = `enhanced-${selectedImage?.hash || 'image'}-${Date.now()}.${enhancedImage.mimeType?.includes('jpeg') ? 'jpg' : 'png'}`;
      const file = new File([blob], fileName, { type: enhancedImage.mimeType || 'image/png' });
      
      // Upload via FormData
      const formData = new FormData();
      formData.append('file', file);
      formData.append('adAccountId', adAccountId);
      formData.append('type', 'image');
      
      const resp = await fetch('https://api.glowytics.com/api/meta-ads/media-library/upload-image', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        },
        body: formData
      });
      
      const data = await resp.json();
      
      if (data.success) {
        toast.success('Image uploaded to Meta Media Library!');
        // Refresh images list
        fetchImages();
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload to Meta error:', err);
      toast.error(err.message || 'Failed to upload to Meta');
    } finally {
      setIsUploadingToMeta(false);
    }
  };

  // Reset enhancement
  const resetEnhancement = () => {
    setEnhancedImage(null);
    setEnhanceError('');
    setSelectedPreset(null);
    setCustomPrompt('');
  };

  // Enhancement Modal
  const EnhanceModal = () => {
    if (!showEnhanceModal || !selectedImage) return null;

    const presetInfo = selectedPreset ? ENHANCEMENT_PRESETS.find(p => p.id === selectedPreset) : null;
    
    return (
      <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={closeEnhanceModal}>
        <div 
          className="bg-white rounded-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center">
                <Wand2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Image Enhancer</h3>
                <p className="text-gray-500 text-sm">Enhance your product images</p>
              </div>
            </div>
            <button 
              onClick={closeEnhanceModal} 
              disabled={isEnhancing}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-auto">
            <div className="p-6">
              {/* Before / After Comparison */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Original */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-gray-400" />
                    <span className="text-sm font-medium text-gray-700">Original</span>
                  </div>
                  <div className="relative bg-gray-100 rounded-xl overflow-hidden aspect-square flex items-center justify-center">
                    <img 
                      src={selectedImage.url || selectedImage.url_128} 
                      alt="Original"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <p className="text-xs text-gray-500 truncate">{selectedImage.name || selectedImage.hash}</p>
                </div>

                {/* Enhanced */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="text-sm font-medium text-gray-700">Enhanced</span>
                    {enhancedImage && (
                      <span className="ml-auto text-xs text-emerald-600 font-medium flex items-center gap-1">
                        <Check className="w-3 h-3" /> Ready
                      </span>
                    )}
                  </div>
                  <div className="relative bg-gray-100 rounded-xl overflow-hidden aspect-square flex items-center justify-center">
                    {isEnhancing ? (
                      <div className="text-center">
                        <div className="relative">
                          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto" />
                          <Sparkles className="w-5 h-5 text-amber-500 absolute -top-1 -right-1 animate-pulse" />
                        </div>
                        <p className="text-gray-600 mt-4 font-medium">AI is working its magic...</p>
                        <p className="text-gray-400 text-sm mt-1">This may take 10-30 seconds</p>
                      </div>
                    ) : enhancedImage ? (
                      <img 
                        src={`data:${enhancedImage.mimeType || 'image/png'};base64,${enhancedImage.data}`}
                        alt="Enhanced"
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : enhanceError ? (
                      <div className="text-center p-4">
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <X className="w-6 h-6 text-red-600" />
                        </div>
                        <p className="text-red-600 text-sm">{enhanceError}</p>
                      </div>
                    ) : (
                      <div className="text-center">
                        <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Sparkles className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-gray-500">Select a preset or enter a prompt</p>
                        <p className="text-gray-400 text-sm mt-1">Then click "Enhance" to start</p>
                      </div>
                    )}
                  </div>
                  {enhancedImage && (
                    <div className="flex gap-2">
                      <button
                        onClick={downloadEnhanced}
                        className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-xs font-medium"
                      >
                        <Download className="w-3.5 h-3.5" />
                        Download
                      </button>
                      <button
                        onClick={uploadToMeta}
                        disabled={isUploadingToMeta}
                        className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-xs font-medium disabled:opacity-50"
                      >
                        {isUploadingToMeta ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Image className="w-3.5 h-3.5" />
                        )}
                        {isUploadingToMeta ? 'Uploading...' : 'Upload to Meta'}
                      </button>
                      <button
                        onClick={resetEnhancement}
                        className="flex items-center justify-center gap-2 py-2 px-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-xs"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Presets */}
              {!enhancedImage && (
                <>
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-1.5">
                      {ENHANCEMENT_PRESETS.map((preset) => {
                        const isSelected = selectedPreset === preset.id;
                        return (
                          <button
                            key={preset.id}
                            onClick={() => { setSelectedPreset(preset.id); setCustomPrompt(''); }}
                            disabled={isEnhancing}
                            className={`px-2 py-1 rounded text-[10px] font-medium transition-all disabled:opacity-50 ${
                              isSelected 
                                ? 'bg-gray-900 text-white' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {preset.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Custom Prompt */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Or Custom Prompt</h4>
                    <div className="relative">
                      <textarea
                        value={customPrompt}
                        onChange={(e) => {
                          setCustomPrompt(e.target.value);
                          if (e.target.value.trim()) setSelectedPreset(null);
                        }}
                        placeholder="Describe how you want to enhance this image..."
                        rows={3}
                        disabled={isEnhancing}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none disabled:bg-gray-50"
                      />
                    </div>
                    {presetInfo && !customPrompt && (
                      <p className="mt-2 text-xs text-gray-500 italic">
                        "{presetInfo.prompt}"
                      </p>
                    )}
                  </div>

                  {/* Enhance Button */}
                  <button
                    onClick={handleEnhance}
                    disabled={isEnhancing || (!selectedPreset && !customPrompt.trim())}
                    className="w-full flex items-center justify-center gap-3 py-3 px-6 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {isEnhancing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Enhancing...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-5 h-5" />
                        Enhance Image
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (connectionLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading connection info...</p>
        </div>
      </div>
    );
  }

  // No connection state
  if (!adAccountId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Image className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Meta Ads Connection Required</h2>
          <p className="text-gray-600 mb-6">
            Please connect your Meta Ads account to access the AI Enhancer.
          </p>
          <a 
            href="/settings" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Go to Settings
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Modal */}
      <EnhanceModal />
      
      <div className="max-w-7xl mx-auto p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center">
              <Wand2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">AI Image Enhancer</h1>
              <p className="text-gray-500 text-sm">{adAccountName}</p>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Toolbar */}
          <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex items-center gap-2">
              <Image className="w-5 h-5 text-indigo-600" />
              <span className="text-gray-900 font-medium">Your Images</span>
              <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs">
                {images.length}
              </span>
            </div>
            
            {/* Right Controls */}
            <div className="flex items-center gap-3 w-full sm:w-auto">
              {/* Search */}
              <div className="relative flex-1 sm:flex-initial">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search images..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              
              {/* View Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
                >
                  <Grid3X3 className={`w-4 h-4 ${viewMode === 'grid' ? 'text-indigo-600' : 'text-gray-500'}`} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
                >
                  <List className={`w-4 h-4 ${viewMode === 'list' ? 'text-indigo-600' : 'text-gray-500'}`} />
                </button>
              </div>
              
              {/* Refresh */}
              <button
                onClick={handleRefresh}
                disabled={loadingImages}
                className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 text-gray-600 ${loadingImages ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 min-h-[400px]">
            {loadingImages && filteredImages.length === 0 ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-3" />
                  <p className="text-gray-500">Loading images...</p>
                </div>
              </div>
            ) : filteredImages.length === 0 ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Image className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="font-medium text-gray-900 mb-1">
                    {searchTerm ? 'No results found' : 'No images yet'}
                  </h3>
                  <p className="text-gray-500 text-sm">
                    {searchTerm ? 'Try a different search term' : 'Upload images to your Meta Ads account'}
                  </p>
                </div>
              </div>
            ) : viewMode === 'grid' ? (
              /* Grid View */
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {filteredImages.map((item) => (
                  <div
                    key={item.id || item.hash}
                    className="group relative aspect-square bg-gray-100 rounded-xl overflow-hidden cursor-pointer hover:ring-2 hover:ring-indigo-500 transition-all"
                  >
                    <img
                      src={item.url_128 || item.url}
                      alt={item.name || 'Image'}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                      <p className="text-white text-xs truncate mb-2">{item.name || item.hash?.slice(0, 12)}</p>
                      <button
                        onClick={() => openEnhanceModal(item)}
                        className="flex items-center justify-center gap-2 w-full py-2 bg-gray-900 text-white rounded-lg text-xs font-medium hover:bg-gray-800 transition-colors"
                      >
                        <Wand2 className="w-3.5 h-3.5" />
                        Enhance with AI
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* List View */
              <div className="space-y-2">
                {filteredImages.map((item) => (
                  <div
                    key={item.id || item.hash}
                    className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 cursor-pointer transition-colors"
                  >
                    {/* Thumbnail */}
                    <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={item.url_128 || item.url}
                        alt={item.name || 'Image'}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate">
                        {item.name || item.hash}
                      </h4>
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                        {item.width && item.height && (
                          <span>{item.width} x {item.height}</span>
                        )}
                        {item.created_time && (
                          <span>{formatDate(item.created_time)}</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => copyToClipboard(item.id || item.hash, 'ID')}
                        className="p-2 hover:bg-white rounded-lg transition-colors"
                      >
                        <Copy className="w-4 h-4 text-gray-500" />
                      </button>
                      <button
                        onClick={() => openEnhanceModal(item)}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
                      >
                        <Wand2 className="w-4 h-4" />
                        Enhance
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Load More */}
            {hasMoreImages && (
              <div className="mt-6 text-center">
                <button
                  onClick={loadMore}
                  disabled={loadingImages}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {loadingImages ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                  Load More
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-100 bg-gray-50">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>{filteredImages.length} images shown</span>
              <span className="text-xs text-gray-400">Select an image to enhance with AI</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

